import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Spinner from "../Spinner";
import ImageThumbnail from "./index";
import { memo, useCallback, useMemo } from 'react'
import useRetryImage from './useRetryImage'

const PreviewImage = styled('div').withConfig({
    shouldForwardProp: (props) => !['width', 'height', 'error'].includes(props),
})`
  background-color: ${({ theme }) => theme.placeholderColor};
  display: flex;
  margin-right: 1rem;
  margin-bottom: .5rem;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: ${({ width }) => width}px;
    height: ${({ height }) => height}px;
    object-fit: cover;
  }

  ${({ error }) =>
        error &&
        `
        border: 2px solid transparent;

        img {
            opacity: 0;
        }

        .photo-thumbnail__processing--failed {
            color: rgb(214, 39, 40);
        }
  `}
`;

const ProcessingLabel = styled.span`
  display: block;
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%,-50%,0);
  text-align: center;
`;

const ImageThumbnailLoader = (props) => {
    const { t } = useTranslation();
    const {
        errorState: [error],
        loadedState: [, setLoaded],
        isLoading,
        mediaIsProcessing,
        ...thumbnailProps
    } = props
    const {
        retryOnError,
        resetRetryOnLoad,
        retryKey,
        retryFailed,
    } = useRetryImage()

    const isProcessing = useMemo(() => mediaIsProcessing, [mediaIsProcessing])
    const onError = useCallback(() => {
        setLoaded(false)
        retryOnError()
    }, [setLoaded, retryOnError])

    const onLoad = useCallback(() => {
        setLoaded(true)
        resetRetryOnLoad()
    }, [setLoaded, resetRetryOnLoad])

    return (
        <PreviewImage
            error={error || isProcessing}
            className="image-thumbnail-loader__preview"
            width={thumbnailProps.width}
            height={thumbnailProps.height}
        >
            {
                retryFailed || mediaIsProcessing
                    ? (
                        <>
                            <ProcessingLabel className={`photo-thumbnail__processing ${retryFailed ? 'photo-thumbnail__processing--failed' : ''}`}>
                                {retryFailed ? t('Media failed to load') : t('Media is processing')}
                            </ProcessingLabel>
                        </>
                    )
                    : null
            }
            {
                isLoading
                    ? <Spinner />
                    : (
                        <ImageThumbnail
                            key={retryKey}
                            onError={onError}
                            onLoad={onLoad}
                            {...thumbnailProps}
                        />
                    )
            }
        </PreviewImage>
    );
};

export default memo(ImageThumbnailLoader);
